import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {Set<Element>} */
  static #pendingSectionNormalize = new Set();

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#scheduleSectionNormalize();

    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
    this.#scheduleSectionNormalize();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * @returns {boolean}
   */
  #isSiteContentAccordion() {
    return this.closest('.accordion') != null;
  }

  /**
   * @returns {Element | null}
   */
  #getSectionRoot() {
    return this.closest('[id^="shopify-section-"], .shopify-section');
  }

  /**
   * @param {Element} section
   * @returns {NodeListOf<HTMLElement>}
   */
  #getSiblingContentAccordions(section) {
    return section.querySelectorAll('.accordion accordion-custom');
  }

  /**
   * Close other site content accordions in the same section when this one opens.
   */
  #closeOtherContentAccordions() {
    if (!this.details.open || !this.#isSiteContentAccordion()) return;

    const section = this.#getSectionRoot();
    if (!section) return;

    for (const accordion of this.#getSiblingContentAccordions(section)) {
      if (accordion === this) continue;

      const details = accordion.querySelector('details');
      if (details instanceof HTMLDetailsElement && details.open) {
        AccordionCustom.#setDetailsClosed(details, { animate: true });
      }
    }
  }

  #handleToggle = () => {
    this.#closeOtherContentAccordions();
  };

  #scheduleSectionNormalize() {
    if (!this.#isSiteContentAccordion()) return;

    const section = this.#getSectionRoot();
    if (!section) return;

    AccordionCustom.#queueSectionNormalize(section);
  }

  /**
   * @param {Element} section
   */
  static #queueSectionNormalize(section) {
    if (AccordionCustom.#pendingSectionNormalize.has(section)) return;

    AccordionCustom.#pendingSectionNormalize.add(section);
    requestAnimationFrame(() => {
      AccordionCustom.#pendingSectionNormalize.delete(section);
      AccordionCustom.#normalizeSectionOpen(section);
    });
  }

  /**
   * Keep at most one open content accordion per section after load or breakpoint change.
   * @param {Element} section
   */
  static #normalizeSectionOpen(section) {
    const openDetails = [...section.querySelectorAll('.accordion accordion-custom details[open]')].filter(
      (element) => element instanceof HTMLDetailsElement
    );

    if (openDetails.length <= 1) return;

    for (let index = 1; index < openDetails.length; index += 1) {
      AccordionCustom.#setDetailsClosed(openDetails[index], { animate: false });
    }
  }

  /**
   * @param {HTMLDetailsElement} details
   * @param {{ animate?: boolean }} [options]
   */
  static #setDetailsClosed(details, { animate = false } = {}) {
    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    if (!animate || details.closest('.accordion') == null) {
      details.open = false;
      return;
    }

    details.classList.add('details--closing');

    requestAnimationFrame(() => {
      details.open = false;

      const content = details.querySelector('.details-content');
      if (!(content instanceof HTMLElement)) {
        details.classList.remove('details--closing');
        return;
      }

      const cleanup = () => {
        details.classList.remove('details--closing');
        content.removeEventListener('transitionend', onTransitionEnd);
      };

      /** @param {TransitionEvent} event */
      const onTransitionEnd = (event) => {
        if (event.target !== content) return;
        cleanup();
      };

      content.addEventListener('transitionend', onTransitionEnd);
      window.setTimeout(cleanup, 800);
    });
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  };
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
